import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../services/firebase';
import { collection, query, getDocs, where, onSnapshot } from 'firebase/firestore';
import {
  Music,
  Calendar,
  ShoppingBag,
  MessageSquare,
  Mic2,
  TrendingUp,
  ArrowUpRight,
  Clock,
} from 'lucide-react';

// Stat Card Component
function StatCard({ title, value, icon: Icon, change, changeType, link }) {
  return (
    <Card className="hover:border-orange-500/50 transition-all cursor-pointer">
      <Link to={link}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-400 mb-1">{title}</p>
              <p className="text-3xl font-bold text-white">{value}</p>
              {change && (
                <p
                  className={`text-sm mt-2 flex items-center gap-1 ${
                    changeType === 'up' ? 'text-green-500' : 'text-red-500'
                  }`}
                >
                  <TrendingUp className={`w-4 h-4 ${changeType === 'down' ? 'rotate-180' : ''}`} />
                  {change}
                </p>
              )}
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-red-600/20 rounded-xl flex items-center justify-center">
              <Icon className="w-6 h-6 text-orange-500" />
            </div>
          </div>
        </CardContent>
      </Link>
    </Card>
  );
}

// Quick Action Card
function QuickAction({ title, description, icon: Icon, link, color }) {
  return (
    <Link to={link}>
      <motion.div
        whileHover={{ scale: 1.02 }}
        className={`p-4 rounded-xl border border-gray-800 hover:border-${color}-500/50 bg-gradient-to-br from-gray-900 to-gray-900/50 cursor-pointer transition-all group`}
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 bg-${color}-500/20 rounded-lg flex items-center justify-center`}>
            <Icon className={`w-5 h-5 text-${color}-500`} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-white group-hover:text-orange-500 transition-colors">
              {title}
            </h3>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
          <ArrowUpRight className="w-5 h-5 text-gray-600 group-hover:text-orange-500 transition-colors" />
        </div>
      </motion.div>
    </Link>
  );
}

// Recent Activity Item
function ActivityItem({ title, description, time, type }) {
  const typeColors = {
    booking: 'bg-blue-500',
    contact: 'bg-green-500',
    order: 'bg-purple-500',
    music: 'bg-orange-500',
  };

  return (
    <div className="flex items-start gap-4 py-3">
      <div className={`w-2 h-2 rounded-full mt-2 ${typeColors[type] || 'bg-gray-500'}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white font-medium truncate">{title}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <div className="flex items-center gap-1 text-xs text-gray-500">
        <Clock className="w-3 h-3" />
        {time}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [greeting, setGreeting] = useState('');
  const [stats, setStats] = useState([
    { title: 'Total Bookings', value: '0', icon: Mic2, link: '/admin/bookings' },
    { title: 'New Messages', value: '0', icon: MessageSquare, link: '/admin/contacts' },
    { title: 'Upcoming Shows', value: '0', icon: Calendar, link: '/admin/shows' },
    { title: 'Music Releases', value: '0', icon: Music, link: '/admin/music' },
  ]);
  const [recentActivity, setRecentActivity] = useState([]);

  // Fetch real data from Firestore
  useEffect(() => {
    if (!db) return;

    // Fetch bookings count
    const fetchBookings = async () => {
      try {
        const bookingsSnapshot = await getDocs(collection(db, 'bookings'));
        const bookingCount = bookingsSnapshot.size;
        setStats(prev => prev.map(stat => 
          stat.title === 'Total Bookings' ? { ...stat, value: bookingCount.toString() } : stat
        ));
      } catch (error) {
        console.error('Error fetching bookings:', error);
      }
    };

    // Fetch contacts/messages count
    const unsubscribeContacts = onSnapshot(
      query(collection(db, 'contacts'), where('read', '==', false)),
      (snapshot) => {
        const messageCount = snapshot.size;
        setStats(prev => prev.map(stat => 
          stat.title === 'New Messages' ? { ...stat, value: messageCount.toString() } : stat
        ));
      },
      (error) => console.error('Error fetching contacts:', error)
    );

    // Fetch shows count
    const fetchShows = async () => {
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const showsSnapshot = await getDocs(collection(db, 'shows'));
        const upcomingShows = showsSnapshot.docs.filter(doc => {
          const showDate = doc.data().date ? new Date(doc.data().date) : null;
          return showDate && showDate >= today && doc.data().status !== 'cancelled';
        });
        setStats(prev => prev.map(stat => 
          stat.title === 'Upcoming Shows' ? { ...stat, value: upcomingShows.length.toString() } : stat
        ));
      } catch (error) {
        console.error('Error fetching shows:', error);
      }
    };

    // Fetch music releases count
    const fetchMusic = async () => {
      try {
        const musicSnapshot = await getDocs(collection(db, 'music'));
        setStats(prev => prev.map(stat => 
          stat.title === 'Music Releases' ? { ...stat, value: musicSnapshot.size.toString() } : stat
        ));
      } catch (error) {
        console.error('Error fetching music:', error);
      }
    };

    // Helper to convert Firestore timestamp to formatted date
    const formatTimestamp = (timestamp) => {
      if (!timestamp) return 'No date';
      // Firestore Timestamp has toDate() method
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Fetch recent activity (bookings and contacts)
    const fetchRecentActivity = async () => {
      try {
        const bookingsSnapshot = await getDocs(
          query(collection(db, 'bookings'))
        );
        const contactsSnapshot = await getDocs(
          query(collection(db, 'contacts'))
        );

        const activities = [];

        // Add recent bookings
        bookingsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.createdAt) {
            const timestamp = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
            activities.push({
              title: `New booking - ${data.artistName || 'Unknown'}`,
              description: `${data.contactName || 'Unknown'} - ${data.venue || 'TBD'}`,
              time: formatTimestamp(data.createdAt),
              timestamp: timestamp.getTime(),
              type: 'booking',
            });
          }
        });

        // Add recent contacts
        contactsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.createdAt && data.category === 'booking') {
            const timestamp = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
            activities.push({
              title: `Booking inquiry - ${data.name || 'Unknown'}`,
              description: data.message?.substring(0, 50) || 'New message',
              time: formatTimestamp(data.createdAt),
              timestamp: timestamp.getTime(),
              type: 'contact',
            });
          }
        });

        // Sort by recency (newest first) and take latest 4
        activities.sort((a, b) => b.timestamp - a.timestamp);
        setRecentActivity(activities.slice(0, 4));
      } catch (error) {
        console.error('Error fetching recent activity:', error);
      }
    };

    fetchBookings();
    fetchShows();
    fetchMusic();
    fetchRecentActivity();

    return () => {
      unsubscribeContacts();
    };
  }, []);

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good morning');
    else if (hour < 18) setGreeting('Good afternoon');
    else setGreeting('Good evening');
  }, []);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  // Stats and recentActivity now managed via Firestore above

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        visible: { transition: { staggerChildren: 0.1 } },
      }}
      className="space-y-8"
    >
      {/* Welcome Section */}
      <motion.div variants={fadeIn}>
        <h2 className="text-2xl font-bold text-white mb-1">
          {greeting}, {user?.displayName?.split(' ')[0] || 'Admin'}!
        </h2>
        <p className="text-gray-400">Here's what's happening with your business today.</p>
      </motion.div>

      {/* Stats Grid */}
      <motion.div
        variants={fadeIn}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {stats.map((stat) => (
          <StatCard key={stat.title} {...stat} />
        ))}
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <motion.div variants={fadeIn} className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <QuickAction
                title="Add Music Release"
                description="Upload new single or album"
                icon={Music}
                link="/admin/music"
                color="orange"
              />
              <QuickAction
                title="Add Show Date"
                description="Schedule upcoming performance"
                icon={Calendar}
                link="/admin/shows"
                color="blue"
              />
              <QuickAction
                title="Manage Inventory"
                description="Update merchandise stock"
                icon={ShoppingBag}
                link="/admin/merch"
                color="purple"
              />
              <QuickAction
                title="View Bookings"
                description="Check pending booking requests"
                icon={Mic2}
                link="/admin/bookings"
                color="green"
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Activity */}
        <motion.div variants={fadeIn}>
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="text-lg">Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              {recentActivity.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">No recent activity</p>
                </div>
              ) : (
                <>
                  <div className="divide-y divide-gray-800">
                    {recentActivity.map((activity, index) => (
                      <ActivityItem key={index} {...activity} />
                    ))}
                  </div>
                  <Link
                    to="/admin/bookings"
                    className="block text-center text-sm text-orange-500 hover:text-orange-400 mt-4"
                  >
                    View all activity
                  </Link>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Upcoming Shows Preview */}
      <motion.div variants={fadeIn}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Upcoming Shows</CardTitle>
            <Link
              to="/admin/shows"
              className="text-sm text-orange-500 hover:text-orange-400"
            >
              View all
            </Link>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">No upcoming shows scheduled</p>
              <Link
                to="/admin/shows"
                className="text-orange-500 hover:text-orange-400 text-sm mt-3 inline-block"
              >
                Add your first show
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
